import React, { useEffect, useState } from 'react';
import {
  Plus, Trash2, Loader2, CheckCircle, AlertCircle,
  ImagePlus, Save, X, Sparkles,
} from 'lucide-react';
import Shell from '../components/TopBarShell';
import NewsletterAdminNav from '../components/NewsletterAdminNav';
import { LANG_LABEL, LangTabs, localizedField, normalizeTranslations, setLocalizedField, tabStatusFor } from '../components/LangTabs';
import { LT } from '../lib/appCatalogue';
import { useLocale, useT } from '../contexts/LocaleProvider';
import api from '../utils/api';

/* MANEVA events CMS · curated upcoming-events list, surfaced on the
 * Splash homepage. HR-gated, edits land via /api/newsletter-events/.
 * One row per event, translated in place (FR canonical, NL / EN in
 * `translations`) · the same model as episodes, so a reader in any
 * language sees every event. */

const EVENT_FIELDS = ['title', 'date_label', 'location', 'description'];

export default function AdminNewsletterEvents() {
  const [allowed, setAllowed] = useState(undefined);
  useEffect(() => {
    api.canEditContent().then(setAllowed).catch(() => setAllowed(false));
  }, []);
  if (allowed === undefined) return <Shell><Loading /></Shell>;
  if (!allowed) return <Shell><NotAllowed /></Shell>;
  return <EventsList />;
}

const Loading = () => {
  const t = useT();
  return (
    <div className="flex items-center gap-2 mt-10 text-[13px]" style={{ color: LT.green50 }}>
      <Loader2 className="h-4 w-4 animate-spin" /> {t('common.loading')}
    </div>
  );
};

const NotAllowed = () => {
  const t = useT();
  return (
    <div className="max-w-[520px] mt-10 rounded-2xl p-6"
         style={{ background: LT.beigeLight, border: `1px dashed ${LT.green30}`, color: LT.green50 }}>
      {t('events.admin.notAllowed')}
    </div>
  );
};

const EventsList = () => {
  const t = useT();
  const [events, setEvents] = useState(null);
  const [adding, setAdding] = useState(false);

  const load = () => api.newsletterEvents()
    .then(rows => setEvents(Array.isArray(rows) ? rows : (rows?.results || [])))
    .catch(() => setEvents([]));
  useEffect(() => { load(); }, []);

  return (
    <Shell>
      <NewsletterAdminNav />
      <header className="mb-6 flex items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-[2rem] sm:text-[2.4rem] leading-tight"
              style={{ color: LT.greenDeep }}>
            {t('events.admin.title')}
          </h1>
          <p className="mt-2 text-[14px] max-w-[640px]" style={{ color: '#3a4f44' }}>
            {t('events.admin.intro')}
          </p>
        </div>
        <button onClick={() => setAdding(true)}
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-[13px] font-medium transition"
                style={{ background: LT.greenDeep, color: '#fefefe' }}>
          <Plus className="h-4 w-4" /> {t('events.admin.add')}
        </button>
      </header>

      {adding && (
        <EventForm t={t} onCancel={() => setAdding(false)}
                   onSaved={() => { setAdding(false); load(); }} />
      )}

      <section className="mt-5">
        {events === null && <Loading />}
        {events && events.length === 0 && (
          <div className="rounded-2xl p-6 text-[14px]"
               style={{ background: LT.beigeLight, border: `1px dashed ${LT.green30}`, color: LT.green50 }}>
            {t('events.admin.empty')}
          </div>
        )}
        <ul className="space-y-3">
          {events?.map(ev => (
            <EventRow key={ev.id} t={t} event={ev} onReload={load} />
          ))}
        </ul>
      </section>

      <style>{`
        .ev-input { background:#fff; border:1px solid ${LT.green30}; border-radius:0.6rem;
                    padding:0.5rem 0.75rem; font-size:13.5px; color:#2b4138; width:100%; }
        .ev-input:focus { outline:none; border-color:${LT.greenDeep};
                          box-shadow:0 0 0 3px rgba(43,65,56,0.08); }
        .ev-textarea { background:#fff; border:1px solid ${LT.green30}; border-radius:0.6rem;
                       padding:0.5rem 0.75rem; font-size:13.5px; color:#2b4138; width:100%;
                       font-family:inherit; line-height:1.55; }
      `}</style>
    </Shell>
  );
};

const blank = () => ({
  title: '', date_label: '', starts_at: '', location: '',
  description: '', url: '', locale: 'fr', show_on_homepage: true, sort_order: 0,
  translations: normalizeTranslations({}, EVENT_FIELDS),
});

const EventRow = ({ t, event, onReload }) => {
  const { intlLocale } = useLocale();
  const [open, setOpen] = useState(false);
  const past = event.starts_at && new Date(event.starts_at) < new Date(new Date().toDateString());
  const langs = ['nl', 'en'].filter(l => (event.translations?.[l]?.title || '').trim());
  if (!open) {
    return (
      <li>
        <button onClick={() => setOpen(true)}
                className="w-full text-left flex items-center gap-4 px-4 py-3 rounded-xl transition hover:-translate-y-px"
                style={{ background: LT.beigeLight, border: `1px solid ${LT.green30}`, opacity: past ? 0.7 : 1 }}>
          <div className="font-num shrink-0 w-24 text-center px-2 py-1 rounded-md text-[11px]"
               style={{ background: LT.yellow, color: LT.greenDeep, fontWeight: 600 }}>
            {event.date_label || '—'}
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-display text-[1rem] leading-tight" style={{ color: LT.greenDeep }}>
              {event.title}
            </div>
            <div className="text-[11.5px] mt-0.5 font-num" style={{ color: LT.green50 }}>
              {event.starts_at
                ? new Intl.DateTimeFormat(intlLocale, { day: 'numeric', month: 'short', year: 'numeric' }).format(new Date(event.starts_at))
                : t('events.admin.noDate')}
              {event.location && ` · ${event.location}`}
              {past && ` · ${t('events.admin.past')}`}
            </div>
          </div>
          {langs.length > 0 && (
            <span className="text-[10.5px] font-num uppercase tracking-[0.1em]" style={{ color: LT.green50 }}
                  title={t('events.admin.translatedIn')}>
              {langs.join(' · ')}
            </span>
          )}
          <span className="text-[10.5px] uppercase tracking-[0.14em] font-medium px-2 py-1 rounded-md font-num"
                style={{
                  background: event.show_on_homepage ? '#e5efe7' : 'rgba(43,65,56,0.06)',
                  color: event.show_on_homepage ? '#3a6f4a' : LT.green50,
                }}>
            {event.show_on_homepage ? t('events.admin.homepage') : t('events.admin.hidden')}
          </span>
        </button>
      </li>
    );
  }
  return (
    <li>
      <EventForm t={t} event={event}
                 onCancel={() => setOpen(false)}
                 onSaved={() => { setOpen(false); onReload(); }}
                 onDeleted={() => { setOpen(false); onReload(); }} />
    </li>
  );
};

const EventForm = ({ t, event = null, onCancel, onSaved, onDeleted }) => {
  const [draft, setDraft] = useState(() => event
    ? { ...event, starts_at: event.starts_at || '', translations: normalizeTranslations(event.translations, EVENT_FIELDS) }
    : blank());
  const [lang, setLang] = useState('fr');
  const [imageFile, setImageFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [translating, setTranslating] = useState(false);
  const [err, setErr] = useState('');
  const [note, setNote] = useState('');

  const field = (name) => localizedField(draft, lang, name);
  const setField = (name, value) => setDraft(d => setLocalizedField(d, lang, name, value));

  const save = async (e) => {
    e?.preventDefault?.();
    setErr(''); setBusy(true);
    try {
      const payload = { ...draft };
      // starts_at empty string → null (DRF expects null, not '')
      if (!payload.starts_at) payload.starts_at = null;
      if (imageFile) payload.image = imageFile; else delete payload.image;
      if (event) await api.adminUpdateEvent(event.id, payload);
      else await api.adminCreateEvent(payload);
      setNote(t('events.admin.saved'));
      onSaved?.();
    } catch (e2) {
      setErr(e2?.response?.data?.detail || JSON.stringify(e2?.response?.data || {}) || e2.message);
    } finally { setBusy(false); }
  };

  const translate = async () => {
    if (lang === 'fr') return;
    setTranslating(true); setErr(''); setNote('');
    try {
      const src = {};
      EVENT_FIELDS.forEach(f => { src[f] = draft[f] || ''; });
      const out = await api.adminTranslate('fr', lang, src);
      setDraft(d => ({
        ...d,
        translations: { ...(d.translations || {}), [lang]: { ...((d.translations || {})[lang] || {}), ...out } },
      }));
      setNote(t('events.admin.translated'));
    } catch (e2) {
      setErr(e2?.response?.data?.detail || e2?.message || t('events.admin.translateFailed'));
    } finally { setTranslating(false); }
  };

  const remove = async () => {
    if (!event || !confirm(t('events.admin.deleteConfirm'))) return;
    await api.adminDeleteEvent(event.id);
    onDeleted?.();
  };

  const status = tabStatusFor({
    ...(draft.translations || {}),
    fr: { title: draft.title, date_label: draft.date_label, location: draft.location, description: draft.description },
  }, EVENT_FIELDS);

  return (
    <form onSubmit={save}
          className="rounded-2xl p-5 grid grid-cols-1 md:grid-cols-6 gap-3"
          style={{ background: LT.beigeLight, border: `1px solid ${LT.green30}` }}>
      <div className="md:col-span-6 flex items-center justify-between gap-3 flex-wrap">
        <LangTabs value={lang} onChange={setLang} status={status} />
        {lang !== 'fr' && (
          <button type="button" onClick={translate} disabled={translating}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[12.5px] font-medium disabled:opacity-50"
                  style={{ background: LT.greenDeep, color: '#fefefe' }}>
            {translating ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
            {t('events.admin.translate')} · {LANG_LABEL[lang]}
          </button>
        )}
      </div>
      <Field label={`${t('events.admin.field.title')} (${LANG_LABEL[lang]})`} required={lang === 'fr'} className="md:col-span-4">
        <input required={lang === 'fr'} value={field('title')} onChange={e => setField('title', e.target.value)}
               className="ev-input" />
      </Field>
      <Field label={`${t('events.admin.field.dateLabel')} (${LANG_LABEL[lang]})`} hint={t('events.admin.field.dateLabel.hint')}
             required={lang === 'fr'} className="md:col-span-2">
        <input required={lang === 'fr'} value={field('date_label')} onChange={e => setField('date_label', e.target.value)}
               className="ev-input" />
      </Field>
      <Field label={t('events.admin.field.startsAt')} hint={t('events.admin.field.startsAt.hint')} required className="md:col-span-2">
        <input type="date" required value={draft.starts_at || ''}
               onChange={e => setDraft({ ...draft, starts_at: e.target.value })}
               className="ev-input" />
      </Field>
      <Field label={`${t('events.admin.field.location')} (${LANG_LABEL[lang]})`} className="md:col-span-3">
        <input value={field('location')} onChange={e => setField('location', e.target.value)}
               className="ev-input" />
      </Field>
      <Field label={t('events.admin.field.order')} hint={t('events.admin.field.order.hint')} className="md:col-span-1">
        <input type="number" value={draft.sort_order}
               onChange={e => setDraft({ ...draft, sort_order: parseInt(e.target.value, 10) || 0 })}
               className="ev-input" />
      </Field>
      <Field label={`${t('events.admin.field.description')} (${LANG_LABEL[lang]})`} className="md:col-span-6">
        <textarea rows={2} value={field('description')} onChange={e => setField('description', e.target.value)}
                  className="ev-textarea" />
      </Field>
      <Field label={t('events.admin.field.url')} className="md:col-span-4">
        <input type="url" value={draft.url}
               onChange={e => setDraft({ ...draft, url: e.target.value })}
               className="ev-input" placeholder="https://…" />
      </Field>
      <Field label={t('events.admin.field.image')} className="md:col-span-2">
        <div className="flex flex-col gap-2">
          {(imageFile || draft.image) && (
            <div className="w-full aspect-video rounded-lg overflow-hidden bg-[#1c2a23]">
              <img src={imageFile ? URL.createObjectURL(imageFile) : draft.image}
                   alt="" className="w-full h-full object-cover" />
            </div>
          )}
          <label className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-[12px] cursor-pointer self-start"
                 style={{ border: `1px solid ${LT.green30}`, color: LT.greenDeep, background: '#fff' }}>
            <ImagePlus className="h-3.5 w-3.5" />
            {imageFile ? imageFile.name : (draft.image ? t('photos.admin.replaceCta') : t('photos.admin.uploadCta'))}
            <input type="file" accept="image/*" className="hidden"
                   onChange={e => setImageFile(e.target.files[0] || null)} />
          </label>
        </div>
      </Field>
      <Field label={t('events.admin.field.showOnHomepage')} className="md:col-span-2">
        <label className="inline-flex items-center gap-2 text-[13px] cursor-pointer mt-1.5"
               style={{ color: LT.greenDeep }}>
          <input type="checkbox" checked={!!draft.show_on_homepage}
                 onChange={e => setDraft({ ...draft, show_on_homepage: e.target.checked })} />
          {t('events.admin.visibleOnHomepage')}
        </label>
      </Field>
      {err && (
        <div className="md:col-span-6 text-[12.5px] flex items-center gap-2" style={{ color: '#b54338' }}>
          <AlertCircle className="h-3.5 w-3.5" /> {err}
        </div>
      )}
      <div className="md:col-span-6 flex items-center gap-3 pt-1">
        <button type="submit" disabled={busy}
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-[13px] font-medium"
                style={{ background: LT.greenDeep, color: '#fefefe' }}>
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
          {event ? t('common.save') : t('events.admin.create')}
        </button>
        {event && (
          <button type="button" onClick={remove}
                  className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-[12.5px] font-medium"
                  style={{ color: '#b54338' }}>
            <Trash2 className="h-3.5 w-3.5" /> {t('common.delete')}
          </button>
        )}
        <button type="button" onClick={onCancel}
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-[12.5px]"
                style={{ color: LT.green50 }}>
          <X className="h-3.5 w-3.5" /> {t('common.cancel')}
        </button>
        {note && (
          <span className="inline-flex items-center gap-1.5 text-[12.5px]" style={{ color: '#3a6f4a' }}>
            <CheckCircle className="h-3.5 w-3.5" /> {note}
          </span>
        )}
      </div>
    </form>
  );
};

const Field = ({ label, hint, children, required, className = '' }) => (
  <label className={`flex flex-col gap-1 ${className}`}>
    <span className="text-[11px] uppercase tracking-[0.12em] font-num font-medium"
          style={{ color: LT.green50 }}>
      {label}{required && <span style={{ color: '#b54338' }}> *</span>}
    </span>
    {children}
    {hint && <span className="text-[11px]" style={{ color: LT.green50 }}>{hint}</span>}
  </label>
);
